import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, HStack, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Switch, Text, VStack } from '@bonusly/design-system';

import { SIDEBAR_ITEMS, SidebarItemConfig, SidebarItemId } from '../config/sidebar-items';

type CustomizeSidebarModalProps = {
	isOpen: boolean;
	onClose: () => void;
	availableItemIds: SidebarItemId[];
	isItemVisible: (itemId: SidebarItemId) => boolean;
	toggleItem: (itemId: SidebarItemId) => void;
};

export const CustomizeSidebarModal: FC<CustomizeSidebarModalProps> = ({ isOpen, onClose, availableItemIds, isItemVisible, toggleItem }) => {
	const { t } = useTranslation('navigation');

	const availableItems = useMemo(() => {
		const availableItems = new Set(availableItemIds);
		return SIDEBAR_ITEMS.filter((item) => availableItems.has(item.id));
	}, [availableItemIds]);

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
			<ModalOverlay />
			<ModalContent bg="bg.surface">
				<ModalHeader>{t('customizeSidebar.title')}</ModalHeader>
				<ModalCloseButton />
				<ModalBody pb={6}>
					<SidebarItemList items={availableItems} isItemVisible={isItemVisible} onToggleItem={toggleItem} />
				</ModalBody>
			</ModalContent>
		</Modal>
	);
};

type SidebarItemListProps = {
	items: SidebarItemConfig[];
	isItemVisible: (itemId: SidebarItemId) => boolean;
	onToggleItem: (itemId: SidebarItemId) => void;
};

const SidebarItemList: FC<SidebarItemListProps> = ({ items, isItemVisible, onToggleItem }) => {
	const { t } = useTranslation('navigation');

	if (items.length === 0) {
		return null;
	}

	return (
		<VStack spacing={0} align="stretch">
			{items.map((item) => {
				const Icon = item.icon;
				const label = t(item.translationKey);
				const isVisible = isItemVisible(item.id);

				return (
					<HStack key={item.id} justifyContent="space-between" alignItems="center" py={2} spacing={4}>
						<HStack spacing={3} flex={1}>
							<Box color="text.default" flexShrink={0}>
								<Icon size="md" color="text.default" data-testid={`customize-sidebar-icon-${item.id}`} />
							</Box>
							<Text fontSize={100} fontWeight="medium" color="text.default">
								{label}
							</Text>
						</HStack>
						<Box>
							<Switch
								size="sm"
								isChecked={isVisible}
								onChange={() => onToggleItem(item.id)}
								aria-label={t('customizeSidebar.toggleLabel', { item: label })}
								data-testid={`customize-sidebar-toggle-${item.id}`}
							/>
						</Box>
					</HStack>
				);
			})}
		</VStack>
	);
};
